// src/pages/DeleteAccount.jsx

import React, { useState } from 'react';
import {
  Link,
  useNavigate,
} from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ShieldExclamationIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function DeleteAccount() {
  const navigate = useNavigate();

  const {
    user,
    isAuthenticated,
    logout,
  } = useAuth();

  const [password, setPassword] =
    useState('');

  const [confirmation, setConfirmation] =
    useState('');

  const [loading, setLoading] =
    useState('');

  const isSchoolAccount =
    Boolean(user?.school_id);

  const canDeleteImmediately =
    user?.role === 'library_user' &&
    !isSchoolAccount;

  const confirmed =
    confirmation.trim().toUpperCase() ===
    'DELETE';

  const getErrorMessage = (
    error,
    fallback
  ) =>
    error.response?.data?.message ||
    fallback;

  const clearSession = async () => {
    try {
      await logout();
    } catch (error) {
      console.error(
        'Logout after deletion failed:',
        error
      );

      localStorage.removeItem('token');
      localStorage.removeItem(
        'accessToken'
      );
      localStorage.removeItem(
        'refreshToken'
      );
      localStorage.removeItem('user');
    }
  };

  const deleteLibraryData = async () => {
    if (!password.trim()) {
      toast.error(
        'Enter your current password.'
      );
      return;
    }

    setLoading('data');

    try {
      const response =
        await apiClient.delete(
          '/users/me/library-data',
          {
            data: {
              password,
            },
          }
        );

      toast.success(
        response.data?.message ||
          'Your library data was deleted.'
      );

      setPassword('');
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          'Your library data could not be deleted.'
        )
      );
    } finally {
      setLoading('');
    }
  };

  const handleAccountDeletion =
    async () => {
      if (!password.trim()) {
        toast.error(
          'Enter your current password.'
        );
        return;
      }

      if (!confirmed) {
        toast.error(
          'Type DELETE to confirm.'
        );
        return;
      }

      setLoading('account');

      try {
        if (canDeleteImmediately) {
          await apiClient.delete(
            '/users/me',
            {
              data: {
                password,
                confirmation: 'DELETE',
              },
            }
          );

          await clearSession();

          navigate(
            '/delete-account?deleted=true',
            {
              replace: true,
            }
          );

          toast.success(
            'Your account was deleted.'
          );
        } else {
          const response =
            await apiClient.post(
              '/users/me/deletion-request',
              {
                password,
                scope: 'account',
              }
            );

          toast.success(
            response.data?.message ||
              'Your deletion request was submitted.'
          );

          setPassword('');
          setConfirmation('');
        }
      } catch (error) {
        toast.error(
          getErrorMessage(
            error,
            canDeleteImmediately
              ? 'Your account could not be deleted.'
              : 'Your request could not be submitted.'
          )
        );
      } finally {
        setLoading('');
      }
    };

  return (
    <div
      className="
        min-h-screen
        bg-gray-50
        text-gray-900
        [padding-top:env(safe-area-inset-top)]
        [padding-bottom:env(safe-area-inset-bottom)]
      "
    >
      {/* Simple responsive top navigation */}
      <header className="sticky top-0 z-20 border-b border-gray-200/80 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-5xl items-center px-4 sm:px-6">
          <Link
            to="/"
            aria-label="PawaLibrary home"
            className="inline-flex items-center gap-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <img
              src="/assets/app-logo.png"
              alt=""
              className="h-9 w-9 object-contain"
            />

            <span className="text-base font-bold tracking-tight text-primary-700">
              PawaLibrary
            </span>
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-xl px-4 py-6 sm:px-6 sm:py-10">
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Heading */}
          <div className="p-5 sm:p-7">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50">
              <TrashIcon className="h-5 w-5 text-red-600" />
            </div>

            <h1 className="mt-4 text-xl font-bold tracking-tight sm:text-2xl">
              Delete account
            </h1>

            <p className="mt-2 text-sm leading-6 text-gray-600">
              Delete your library activity or
              permanently remove your account.
            </p>

            <div className="mt-5 flex items-start gap-3 rounded-xl bg-amber-50 p-3.5">
              <ShieldExclamationIcon className="mt-0.5 h-5 w-5 flex-none text-amber-600" />

              <p className="text-sm leading-5 text-amber-800">
                Account deletion is permanent
                and cannot be undone. Some
                transaction or school records
                may be retained where required.
              </p>
            </div>
          </div>

          {!isAuthenticated ? (
            <div className="border-t border-gray-100 bg-gray-50/60 p-5 sm:p-7">
              <h2 className="font-semibold">
                Sign in to continue
              </h2>

              <p className="mt-1.5 text-sm leading-6 text-gray-600">
                Sign in so we can confirm that
                this account belongs to you.
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <Link
                  to="/login"
                  state={{
                    from: {
                      pathname:
                        '/delete-account',
                    },
                  }}
                  className="
                    inline-flex min-h-12 items-center justify-center
                    rounded-xl bg-primary-600 px-5 py-3
                    text-sm font-semibold text-white
                    transition-colors hover:bg-primary-700
                    focus:outline-none focus:ring-2
                    focus:ring-primary-200
                  "
                >
                  Sign in
                </Link>

                <a
                  href="mailto:support@pawalibrary.com?subject=Account%20deletion%20request"
                  className="
                    inline-flex min-h-12 items-center justify-center
                    rounded-xl border border-gray-300 bg-white
                    px-5 py-3 text-sm font-semibold text-gray-700
                    transition-colors hover:bg-gray-50
                  "
                >
                  Contact support
                </a>
              </div>
            </div>
          ) : (
            <div className="border-t border-gray-100">
              {/* User verification */}
              <div className="p-5 sm:p-7">
                <p className="text-sm text-gray-500">
                  Signed in as
                </p>

                <p className="mt-0.5 truncate text-sm font-semibold text-gray-900">
                  {user?.email ||
                    'Current account'}
                </p>

                <label className="mt-5 block">
                  <span className="text-sm font-medium text-gray-700">
                    Current password
                  </span>

                  <input
                    type="password"
                    value={password}
                    autoComplete="current-password"
                    onChange={(event) =>
                      setPassword(
                        event.target.value
                      )
                    }
                    placeholder="Enter your password"
                    className="
                      mt-2 min-h-12 w-full rounded-xl
                      border border-gray-300 bg-white
                      px-4 py-3 text-base outline-none
                      transition
                      placeholder:text-gray-400
                      focus:border-primary-500
                      focus:ring-2 focus:ring-primary-100
                    "
                  />
                </label>
              </div>

              {/* Delete library activity */}
              <div className="border-t border-gray-100 p-5 sm:p-7">
                <h2 className="font-semibold">
                  Clear library activity
                </h2>

                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Removes favourites, reading
                  progress and activity. Your
                  account remains active.
                </p>

                <button
                  type="button"
                  onClick={deleteLibraryData}
                  disabled={Boolean(loading)}
                  className="
                    mt-4 min-h-12 w-full rounded-xl
                    border border-red-200 bg-white
                    px-5 py-3 text-sm font-semibold
                    text-red-600 transition-colors
                    hover:bg-red-50
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                    sm:w-auto
                  "
                >
                  {loading === 'data'
                    ? 'Clearing activity...'
                    : 'Clear library activity'}
                </button>
              </div>

              {/* Delete account */}
              <div className="border-t border-gray-100 bg-red-50/30 p-5 sm:p-7">
                <h2 className="font-semibold text-red-700">
                  {canDeleteImmediately
                    ? 'Permanently delete account'
                    : 'Request account deletion'}
                </h2>

                <p className="mt-1 text-sm leading-6 text-gray-600">
                  {canDeleteImmediately
                    ? 'This permanently removes your PawaLibrary account and personal data.'
                    : 'Because this account is linked to a school, your request will be reviewed before the account is removed.'}
                </p>

                <label className="mt-4 block">
                  <span className="text-sm font-medium text-gray-700">
                    Type DELETE to confirm
                  </span>

                  <input
                    type="text"
                    value={confirmation}
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    onChange={(event) =>
                      setConfirmation(
                        event.target.value
                      )
                    }
                    placeholder="DELETE"
                    className="
                      mt-2 min-h-12 w-full rounded-xl
                      border border-gray-300 bg-white
                      px-4 py-3 text-base uppercase
                      outline-none transition
                      placeholder:text-gray-400
                      focus:border-red-500
                      focus:ring-2 focus:ring-red-100
                    "
                  />
                </label>

                <button
                  type="button"
                  onClick={
                    handleAccountDeletion
                  }
                  disabled={
                    Boolean(loading) ||
                    !confirmed
                  }
                  className="
                    mt-4 min-h-12 w-full rounded-xl
                    bg-red-600 px-5 py-3
                    text-sm font-semibold text-white
                    transition-colors hover:bg-red-700
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                    sm:w-auto
                  "
                >
                  {loading === 'account'
                    ? 'Please wait...'
                    : canDeleteImmediately
                      ? 'Delete my account'
                      : 'Submit deletion request'}
                </button>
              </div>
            </div>
          )}
        </section>

        <p className="px-4 py-5 text-center text-xs leading-5 text-gray-500">
          Need help?{' '}
          <a
            href="mailto:support@pawalibrary.com"
            className="font-semibold text-primary-700 hover:underline"
          >
            support@pawalibrary.com
          </a>
        </p>
      </main>
    </div>
  );
}